// "Ask for stock" and "Send back to store" (kitchen flow, lane C). A big,
// searchable item list; tap to add, then +/− per line. A barcode scanner (USB
// gun anywhere on the screen, or the Scan button's camera) adds the item — a
// pack barcode adds its pack size. A code typed into the search box and Enter
// does the same. The unsent list is kept on this tablet for this person, so
// leaving the screen does not lose it (Batch 5 lane tablet).
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Search, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { fmtQty } from '@/lib/quantity'
import { T } from '@/lib/inventoryFlow/strings'
import { addPackToDraft, askDraftKey, ITEM_PAGE, loadAskDraft, saveAskDraft, type AskDraft } from '@/lib/inventoryFlow/askDraft'
import { currentQueueUser } from '@/lib/inventoryFlow/offlineQueue'
import type { FlowItem, FlowLocation, RequisitionKind } from '@/lib/inventoryFlow/types'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { ItemAvatar } from './StatusChip'
import { QtyStepper, stepFor } from './QtyStepper'

export function RequestBuilder({
  kind,
  busy,
  onSend,
}: {
  kind: RequisitionKind
  busy: boolean
  onSend: (body: { lines: { stock_item_id: string; qty: number }[]; needed_by?: string; notes?: string }) => Promise<boolean>
}) {
  const { data: itemsRes, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['inventory-flow', 'items'],
    queryFn: () => apiClient.getFlowItems(),
    staleTime: 60_000,
  })
  const { data: locRes } = useQuery({
    queryKey: ['inventory-flow', 'locations'],
    queryFn: () => apiClient.getFlowLocations(),
    staleTime: 5 * 60_000,
  })
  const items = useMemo(() => itemsRes?.data ?? [], [itemsRes])
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items])
  const locations: FlowLocation[] = locRes?.data ?? []
  const store = locations.find((l) => l.is_receiving_default)
  const kitchen = locations.find((l) => l.is_sale_source)

  // The list, kept on this tablet for this person until it is sent.
  const [draftKey] = useState(() => askDraftKey(kind, currentQueueUser()?.id ?? ''))
  const [draft, setDraft] = useState<AskDraft>(() => loadAskDraft(draftKey))
  const [restored, setRestored] = useState(() => draft.lines.length > 0)
  useEffect(() => saveAskDraft(draftKey, draft), [draftKey, draft])
  const setLines = (f: (ls: AskDraft['lines']) => AskDraft['lines']) => setDraft((d) => ({ ...d, lines: f(d.lines) }))

  const [q, setQ] = useState('')
  const [limit, setLimit] = useState(ITEM_PAGE)
  useEffect(() => setLimit(ITEM_PAGE), [q])

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    const list = t
      ? items.filter((i) => i.name.toLowerCase().includes(t) || (i.code ?? '').toLowerCase().includes(t) || (i.category_name ?? '').toLowerCase().includes(t))
      : items
    // For a return, only what the kitchen actually holds is worth showing.
    return kind === 'return' && kitchen ? list.filter((i) => (i.balances[kitchen.id] ?? 0) > 0) : list
  }, [items, q, kind, kitchen])

  // Lines whose item is no longer in the list (archived since) are dropped.
  const lines = draft.lines.flatMap((l) => {
    const item = byId.get(l.id)
    return item ? [{ item, qty: l.qty }] : []
  })
  const inList = new Set(draft.lines.map((l) => l.id))
  const held = (i: FlowItem, locId?: string) => (locId ? i.balances[locId] ?? 0 : 0)

  const add = (item: FlowItem) => {
    setLines((ls) => (ls.some((l) => l.id === item.id) ? ls : [...ls, { id: item.id, qty: stepFor(item.unit) }]))
  }

  const scanner = useItemScanner({
    enabled: items.length > 0,
    onItem: (match) => {
      const item = byId.get(match.item.id)
      if (!item) return { ok: false, text: T.scanUnknownItem(match.item.name) }
      if (kind === 'return' && kitchen && held(item, kitchen.id) <= 0) return { ok: false, text: T.scanNotHeld(item.name) }
      const before = draft.lines.find((l) => l.id === item.id)?.qty ?? 0
      // Functional update: a second scan before the redraw adds on top.
      setLines((ls) => addPackToDraft(ls, item.id, match.pack_qty))
      const pack = match.pack_qty > 0 ? match.pack_qty : 1
      return { ok: true, text: T.scanAddedAsk(item.name, fmtQty(before + pack), item.unit) }
    },
  })

  const onSearchEnter = () => {
    const code = q.trim()
    if (!code) return
    const hit = items.find((i) => (i.code ?? '').toLowerCase() === code.toLowerCase()) ?? (filtered.length === 1 ? filtered[0] : undefined)
    if (hit) {
      add(hit)
      setQ('')
      return
    }
    // Not an item code: try it as a barcode typed by hand.
    if (/^\d{6,}$/.test(code)) {
      setQ('')
      void scanner.lookup(code)
    }
  }

  const send = async () => {
    const ok = await onSend({
      lines: lines.filter((l) => l.qty > 0).map((l) => ({ stock_item_id: l.item.id, qty: l.qty })),
      needed_by: draft.neededBy || undefined,
      notes: draft.notes.trim() || undefined,
    })
    if (ok) {
      setDraft({ lines: [], neededBy: '', notes: '' })
      setRestored(false)
    }
  }

  const shown = filtered.slice(0, limit)

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_minmax(20rem,26rem)]">
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSearchEnter()}
            placeholder={T.search} className="pl-10" aria-label={T.search} />
        </div>
        {scanner.ui}
        {isLoading ? (
          <p className="text-muted-foreground">{T.loading}</p>
        ) : isError ? (
          <div className="space-y-3 rounded-lg border p-4">
            <p className="text-muted-foreground">{T.loadFailed}</p>
            <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>{T.retry}</Button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground">{T.noItems}</p>
        ) : (
          <>
            <ul className="grid max-h-[60vh] gap-2 overflow-y-auto sm:grid-cols-2">
              {shown.map((i) => {
                const added = inList.has(i.id)
                return (
                  <li key={i.id}>
                    <button type="button" onClick={() => add(i)} disabled={added}
                      className="flex w-full items-center gap-3 rounded-lg border bg-card p-2.5 text-left transition-colors hover:border-primary disabled:opacity-60 touch-manipulation">
                      <ItemAvatar name={i.name} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{i.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {kind === 'issue'
                            ? `${T.storeHas} ${fmtQty(held(i, store?.id))} ${i.unit} · ${T.kitchenHas} ${fmtQty(held(i, kitchen?.id))}`
                            : `${T.kitchenHas} ${fmtQty(held(i, kitchen?.id))} ${i.unit}`}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-primary">
                        {added ? <Check className="h-4 w-4" aria-label={T.inList} /> : T.add}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
            {filtered.length > shown.length && (
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span>{T.showingOf(shown.length, filtered.length)}</span>
                <Button variant="outline" size="sm" onClick={() => setLimit((n) => n + ITEM_PAGE)}>{T.showMore}</Button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="space-y-3 rounded-lg border bg-card p-4">
        <h3 className="text-base font-semibold">{T.yourList}</h3>
        {restored && lines.length > 0 && <p className="text-sm text-muted-foreground">{T.restoredList}</p>}
        {lines.length === 0 ? (
          <p className="text-muted-foreground">{T.emptyList}</p>
        ) : (
          <ul className="space-y-3">
            {lines.map((l) => {
              const kitchenHeld = held(l.item, kitchen?.id)
              return (
                <li key={l.item.id} className="space-y-1.5 border-b pb-3 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{l.item.name}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-10 w-10" aria-label={`Remove ${l.item.name}`}
                      onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.item.id))}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <QtyStepper value={l.qty} unit={l.item.unit} label={l.item.name}
                    max={kind === 'return' ? Math.max(kitchenHeld, 0) : undefined}
                    onChange={(v) => setLines((ls) => ls.map((x) => (x.id === l.item.id ? { ...x, qty: v } : x)))} />
                </li>
              )
            })}
          </ul>
        )}
        {kind === 'issue' && (
          <div className="space-y-1">
            <Label htmlFor="rb-needed">{T.neededBy}</Label>
            <Input id="rb-needed" type="date" value={draft.neededBy} onChange={(e) => setDraft((d) => ({ ...d, neededBy: e.target.value }))} />
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="rb-notes">{T.note}</Label>
          <Textarea id="rb-notes" value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} rows={2} />
        </div>
        <Button type="button" className="w-full" disabled={busy || lines.every((l) => l.qty <= 0)} onClick={() => void send()}>
          {busy ? T.sending : kind === 'return' ? T.sendReturn : T.sendRequest}
        </Button>
      </div>
    </div>
  )
}
